import React, { useEffect, useState } from 'react';

import { useDispatch } from 'react-redux';

import Tooltip from 'common-ui/tooltip';
import { getOffers, getProducts } from 'features/newsales/newsalesActions';
import LeadSourceImage from 'pages/settings/salesNewSettings/components/LeadSourceImage';

import { useLeadForm } from '../LeadFormContext';
import {
  CardSubText,
  CardTitle,
  Container,
  ErrorMessage,
  IconWrapper,
  LoadingSpinner,
  MainInfoContent,
  ProductCard,
  ProductCardWrapper,
  ProductGrid,
  SubTitle,
  Title,
} from './ProductInfo.styles';

const ProductInfo = ({ onValidationChange, shouldValidate }) => {
  const dispatch = useDispatch();
  const { formData, updateFormData } = useLeadForm();
  const [selectedProducts, setSelectedProducts] = useState(formData.productIds || []);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showError, setShowError] = useState(false);

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);

      try {
        const productResult = await dispatch(
          getProducts({
            status: 'enabled',
            limit: 100,
          })
        );

        const productsData = productResult.payload?.data || [];

        // Fetch all offers with pagination (limit 12 per page)
        let allOffers = [];
        let page = 1;
        const limit = 12;
        let hasMore = true;

        while (hasMore) {
          const params = {
            status: 'enabled',
            page,
            limit,
          };

          const offersResult = await dispatch(getOffers(params));
          const responseData = offersResult.payload?.data || [];
          allOffers = [...allOffers, ...responseData];

          hasMore = responseData.length === limit;
          page += 1;
        }

        const productIdsWithOffers = new Set(allOffers.map((offer) => offer.productId));

        const filteredProducts = productsData.filter((product) =>
          productIdsWithOffers.has(product.id)
        );

        setProducts(filteredProducts);
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [dispatch]);

  const handleProductClick = (productId) => {
    setSelectedProducts((prevSelected) => {
      if (prevSelected.includes(productId)) {
        return prevSelected.filter((id) => id !== productId);
      } else {
        return [...prevSelected, productId];
      }
    });
    setShowError(false);
  };

  useEffect(() => {
    updateFormData({ productIds: selectedProducts });
  }, [selectedProducts]);

  useEffect(() => {
    const isValid = selectedProducts.length > 0;
    if (onValidationChange) {
      onValidationChange(isValid);
    }
  }, [selectedProducts]);

  useEffect(() => {
    if (shouldValidate && selectedProducts.length === 0) {
      setShowError(true);
    }
  }, [shouldValidate, selectedProducts]);

  return (
    <Container>
      <Title>Select Your Product</Title>
      <SubTitle>Select product</SubTitle>
      <MainInfoContent>
        {loading ? (
          <LoadingSpinner>Loading products...</LoadingSpinner>
        ) : (
          <>
            <ProductGrid>
              {products.map((product, index) => {
                const isCurrentActive = selectedProducts.includes(product.id);

                const isDisabled = product.offerCount === 0;

                return (
                  <ProductCardWrapper key={product.id}>
                    <ProductCard
                      isActive={isCurrentActive}
                      isDisabled={isDisabled}
                      $columnIndex={index % 4}
                      onClick={isDisabled ? undefined : () => handleProductClick(product.id)}
                    >
                      <IconWrapper>
                        <LeadSourceImage
                          imagePath={product.image}
                          alt={product.name}
                          size={64}
                        />
                      </IconWrapper>

                      <CardTitle $columnIndex={index % 4} title={product.name}>
                        {product.name}
                      </CardTitle>
                      {product.description && (
                        <CardSubText $columnIndex={index % 4} title={product.description}>
                          {product.description}
                        </CardSubText>
                      )}
                    </ProductCard>
                    {isDisabled && <Tooltip text="This product  doesn't have any offers" />}
                  </ProductCardWrapper>
                );
              })}
            </ProductGrid>
            {showError && selectedProducts.length === 0 && (
              <ErrorMessage>Please select at least one product to continue</ErrorMessage>
            )}
          </>
        )}
      </MainInfoContent>
    </Container>
  );
};

export default ProductInfo;
